/**
 * Client API Logs Service
 * Fetches from GET /api/v3/history/api-logs
 * Used by /history/api page to display detailed request/response logs
 * from the client_api_logs table (replaces activity_logs for this page)
 */

import axiosInstance from '@/lib/api/axios-instance'

// ─── Types: List ──────────────────────────────────────────────────────────────

export interface ApiLogItem {
  id: string
  requestId: string
  endpoint: string
  method: string
  apiBank: string | null
  status: number | null
  processingTimeMs: number | null
  errorCode: string | null
  errorMessage: string | null
  verificationId: string | null
  generationId: string | null
  ipAddress: string | null
  createdAt: string
}

// ─── Types: Detail ────────────────────────────────────────────────────────────

export interface ApiLogDetail {
  id: string
  customerId: string
  apiKeyId: string | null
  requestId: string
  correlationId: string | null
  endpoint: string
  method: string
  requestHeaders: Record<string, any>
  requestBody: Record<string, any>
  responseStatus: number | null
  responseBody: Record<string, any>
  processingTimeMs: number | null
  errorCode: string | null
  errorMessage: string | null
  verificationId: string | null
  generationId: string | null
  bankResponse: Record<string, any>
  ipAddress: string | null
  userAgent: string | null
  createdAt: string
}

// ─── Types: Filters & Pagination ─────────────────────────────────────────────

export interface ApiLogFilters {
  page?: number
  limit?: number
  endpoint?: string        // iLike filter on endpoint path
  status?: number          // HTTP status code (e.g. 200, 400, 500)
  startDate?: string
  endDate?: string
  requestId?: string
  verificationId?: string
  generationId?: string
  customerId?: string      // elevated users only
}

export interface ApiLogPagination {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
}

export interface ApiLogsListResponse {
  success: boolean
  data: {
    logs: ApiLogItem[]
    pagination: ApiLogPagination
  }
}

export interface ApiLogDetailResponse {
  success: boolean
  data: {
    log: ApiLogDetail
  }
}

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * Fetch paginated list of API call logs.
 * Corresponds to GET /api/v3/history/api-logs
 */
export async function listApiLogs(
  filters: ApiLogFilters = {}
): Promise<ApiLogsListResponse> {
  const params: Record<string, string | number> = {}

  if (filters.page !== undefined) params.page = filters.page
  if (filters.limit !== undefined) params.limit = filters.limit
  if (filters.endpoint) params.endpoint = filters.endpoint
  if (filters.status !== undefined) params.status = filters.status
  if (filters.startDate) params.startDate = filters.startDate
  if (filters.endDate) params.endDate = filters.endDate
  if (filters.requestId) params.requestId = filters.requestId
  if (filters.verificationId) params.verificationId = filters.verificationId
  if (filters.generationId) params.generationId = filters.generationId
  if (filters.customerId) params.customerId = filters.customerId

  const response = await axiosInstance.get<ApiLogsListResponse>(
    '/api/v3/history/api-logs',
    { params }
  )
  return response.data
}

/**
 * Fetch full detail of a single API log including request/response bodies.
 * Corresponds to GET /api/v3/history/api-logs/:logId
 */
export async function getApiLogDetail(logId: string): Promise<ApiLogDetailResponse> {
  const response = await axiosInstance.get<ApiLogDetailResponse>(
    `/api/v3/history/api-logs/${logId}`
  )
  return response.data
}
